import { TFunction } from 'i18next'
import Papa from 'papaparse'

import { productPayloadColumns } from '@features/products/constants/productPayloadColumns'
import { ProductType } from '@features/products/enums/productType'
import { ProductPayload } from '@features/products/types/productPayload'
import { isInvoiceProductType } from '@features/products/utils/isInvoiceProductType'

import { InvalidProductTypeRow } from '../types'

interface ParsedProductsImportCsv {
  invalidProductTypeRows: InvalidProductTypeRow[]
  payloads: ProductPayload[]
  submitted: number
}

interface ProductImportRow {
  payload: ProductPayload
  rowNumber: number
  productType?: string
}

const getColumnByHeader = (header: string, t: TFunction): keyof ProductPayload | string =>
  productPayloadColumns.find(({ key, labelKey }) => key === header || t(labelKey).trim() === header)?.key ?? header

const isEmptyRow = (row: Record<string, string | undefined>): boolean =>
  Object.values(row).every((value) => !value?.trim())

const parseString = (value?: string): string | undefined => value?.trim() || undefined

export const parseProductTypeForImport = (value?: string): ProductType | undefined => {
  const normalizedValue = parseString(value)?.toUpperCase()

  return isInvoiceProductType(normalizedValue) ? normalizedValue : undefined
}

const parseNumber = (value?: string): number | undefined => {
  const trimmed = value?.trim().replace(',', '.')

  if (!trimmed) {
    return undefined
  }

  const parsed = Number(trimmed)

  return Number.isFinite(parsed) ? parsed : undefined
}

const transformToImportRow = (row: Record<string, string | undefined>, rowIndex: number): ProductImportRow => {
  const productType = parseString(row.productType)

  return {
    payload: {
      name: parseString(row.name),
      productType: parseProductTypeForImport(productType),
      description: parseString(row.description),
      unit: parseString(row.unit),
      price: parseNumber(row.price) ?? 0,
      vatRateId: parseString(row.vatRateId),
      accountId: parseString(row.accountId),
    },
    productType,
    rowNumber: rowIndex + 2,
  }
}

export const parseProductsImportCsv = (file: File, t: TFunction): Promise<ParsedProductsImportCsv> =>
  new Promise((resolve, reject) => {
    Papa.parse<Record<string, string | undefined>>(file, {
      header: true,
      transformHeader: (header) => getColumnByHeader(header.trim(), t),
      complete: (results) => {
        const rows = results.data
          .map((row, rowIndex) => ({ row, rowIndex }))
          .filter(({ row }) => !isEmptyRow(row))
          .map(({ row, rowIndex }) => transformToImportRow(row, rowIndex))
        const invalidProductTypeRows = rows.flatMap(({ productType, rowNumber }) =>
          productType && !parseProductTypeForImport(productType) ? [{ rowNumber, value: productType }] : [],
        )

        resolve({
          invalidProductTypeRows,
          payloads: rows
            .filter(({ productType }) => !productType || Boolean(parseProductTypeForImport(productType)))
            .map(({ payload }) => payload),
          submitted: rows.length,
        })
      },
      error: (error) => reject(error),
    })
  })
